import React from 'react';
import { Link } from 'react-router-dom';

const PublicAnalytics: React.FC = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            {/* Hero Section */}
            <div className="relative overflow-hidden py-24">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23EF5000%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>

                <div className="relative px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="mb-6 text-5xl font-bold leading-tight text-white sm:text-6xl">
                            Powerful <span className="bg-gradient-to-r from-primary-400 to-orange-400 bg-clip-text text-transparent">Analytics</span>
                        </h1>
                        <p className="max-w-2xl mx-auto mb-10 text-xl text-gray-300 leading-relaxed">
                            Track every click, understand your audience, and optimize your URL performance with comprehensive analytics tools.
                        </p>
                    </div>
                </div>
            </div>

            {/* Analytics Features Grid */}
            <div className="relative py-24 bg-gray-800/30 backdrop-blur-sm">
                <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
                    <div className="grid gap-8 lg:grid-cols-2 xl:grid-cols-3">

                        {/* Real-time Tracking */}
                        <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
                            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-macos-lg shadow-macos">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="mb-4 text-xl font-semibold text-white">Real-time Tracking</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Monitor clicks as they happen with live updates and instant notifications for your shortened URLs.
                            </p>
                            <ul className="text-sm text-gray-400 space-y-2">
                                <li>• Live click notifications</li>
                                <li>• Real-time dashboard updates</li>
                                <li>• Instant performance metrics</li>
                            </ul>
                        </div>

                        {/* Geographic Analytics */}
                        <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
                            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-macos-lg shadow-macos">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="mb-4 text-xl font-semibold text-white">Geographic Insights</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Understand where your audience is coming from with detailed country and city-level analytics.
                            </p>
                            <ul className="text-sm text-gray-400 space-y-2">
                                <li>• Country-level breakdown</li>
                                <li>• City-level insights</li>
                                <li>• Interactive world map</li>
                            </ul>
                        </div>

                        {/* Device Analytics */}
                        <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
                            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-macos-lg shadow-macos">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="mb-4 text-xl font-semibold text-white">Device & Browser Data</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Get detailed insights into the devices, browsers, and operating systems your audience uses.
                            </p>
                            <ul className="text-sm text-gray-400 space-y-2">
                                <li>• Device type breakdown</li>
                                <li>• Browser analytics</li>
                                <li>• Operating system data</li>
                            </ul>
                        </div>

                        {/* Referrer Tracking */}
                        <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
                            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-macos-lg shadow-macos">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="mb-4 text-xl font-semibold text-white">Referrer Sources</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Track where your traffic is coming from with detailed referrer analysis and source attribution.
                            </p>
                            <ul className="text-sm text-gray-400 space-y-2">
                                <li>• Social media tracking</li>
                                <li>• Email campaign analysis</li>
                                <li>• Direct traffic insights</li>
                            </ul>
                        </div>

                        {/* Time-based Analytics */}
                        <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
                            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-macos-lg shadow-macos">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="mb-4 text-xl font-semibold text-white">Time-based Insights</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Understand when your audience is most active with hourly, daily, and weekly analytics patterns.
                            </p>
                            <ul className="text-sm text-gray-400 space-y-2">
                                <li>• Hourly click patterns</li>
                                <li>• Daily performance trends</li>
                                <li>• Weekly activity analysis</li>
                            </ul>
                        </div>

                        {/* Eddystone Analytics */}
                        <div className="p-8 bg-gray-800/50 border border-gray-700 rounded-macos-xl backdrop-blur-sm hover:bg-gray-700/50 transition-all duration-300">
                            <div className="flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-br from-primary-500 to-orange-500 rounded-macos-lg shadow-macos">
                                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                </svg>
                            </div>
                            <h3 className="mb-4 text-xl font-semibold text-white">Eddystone Beacon Analytics</h3>
                            <p className="text-gray-300 leading-relaxed mb-4">
                                Specialized analytics for IoT deployments with beacon-specific metrics and optimization insights.
                            </p>
                            <ul className="text-sm text-gray-400 space-y-2">
                                <li>• Beacon performance tracking</li>
                                <li>• 16-byte URL optimization</li>
                                <li>• IoT device compatibility</li>
                            </ul>
                        </div>

                    </div>
                </div>
            </div>

            {/* Analytics Benefits Section */}
            <div className="relative py-24">
                <div className="px-4 mx-auto sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">

                    {/* Data-Driven Decisions */}
                    <div className="mb-16">
                        <div className="grid gap-8 lg:grid-cols-2 items-center">
                            <div>
                                <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
                                    Make Data-Driven Decisions
                                </h2>
                                <p className="text-lg text-gray-300 mb-6">
                                    Transform raw click data into actionable insights that help you optimize your campaigns and understand your audience better.
                                </p>
                                <ul className="space-y-3 text-gray-300">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Identify peak engagement times
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Optimize for target demographics
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Track campaign effectiveness
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-gradient-to-br from-primary-500/20 to-orange-500/20 border border-primary-500/30 rounded-macos-xl p-8 backdrop-blur-sm">
                                <div className="text-center">
                                    <div className="text-4xl font-bold text-white mb-2">99.9%</div>
                                    <div className="text-gray-300 mb-4">Data Accuracy</div>
                                    <div className="text-2xl font-bold text-primary-400 mb-2">&lt; 100ms</div>
                                    <div className="text-gray-300 mb-4">Real-time Updates</div>
                                    <div className="text-2xl font-bold text-green-400 mb-2">24/7</div>
                                    <div className="text-gray-300">Monitoring</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Export & Integration */}
                    <div className="mb-16">
                        <div className="grid gap-8 lg:grid-cols-2 items-center">
                            <div className="order-2 lg:order-1">
                                <div className="bg-gray-800/50 border border-gray-700 rounded-macos-xl p-8 backdrop-blur-sm">
                                    <h3 className="text-lg font-semibold text-white mb-6">Export Options</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-macos">
                                            <span className="text-gray-300">CSV Export</span>
                                            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-macos">
                                            <span className="text-gray-300">JSON API</span>
                                            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded-macos">
                                            <span className="text-gray-300">Webhooks</span>
                                            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="order-1 lg:order-2">
                                <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
                                    Export & Integrate
                                </h2>
                                <p className="text-lg text-gray-300 mb-6">
                                    Get your analytics data where you need it with flexible export options and seamless integrations.
                                </p>
                                <ul className="space-y-3 text-gray-300">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Automated report generation
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Third-party integrations
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Custom dashboard embedding
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* CTA Section */}
            <div className="relative py-24">
                <div className="px-4 mx-auto text-center sm:max-w-xl md:max-w-full lg:max-w-screen-xl md:px-24 lg:px-8">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="mb-6 text-3xl font-bold text-white sm:text-4xl">
                            Start tracking your URLs today
                        </h2>
                        <p className="mb-10 text-lg text-gray-300">
                            Get detailed analytics and insights for all your shortened URLs with our comprehensive tracking tools.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/register"
                                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-300 bg-gradient-to-r from-primary-500 to-orange-500 rounded-macos-lg hover:from-primary-600 hover:to-orange-600 shadow-macos-lg hover:shadow-macos-xl hover:scale-105"
                            >
                                <span className="relative z-10">Start Free Trial</span>
                                <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </Link>
                            <Link
                                to="/features"
                                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-gray-300 transition-all duration-300 border border-gray-600 rounded-macos-lg hover:text-white hover:border-gray-500 hover:bg-gray-800/50 backdrop-blur-sm"
                            >
                                View All Features
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicAnalytics;